import React from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, Typography, TextField, Checkbox, FormControlLabel, Grid, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useWizardStore } from '../../store/slices/wizardSlice';
import { FormComponentProps, EmploymentIncomeData } from '../../types/forms';

const IncomeSource = styled(Box)(({ }) => ({
  backgroundColor: '#fff',
  border: '2px solid #e9ecef',
  borderRadius: '12px',
  padding: '25px',
  marginBottom: '20px',
}));

const SectionDivider = styled(Divider)(({ }) => ({
  margin: '30px 0',
  paddingTop: '30px',
}));

const schema = yup.object({
  numJobs: yup.number().min(0).required(),
  jobs: yup.array().of(
    yup.object({
      employerName: yup.string().when('numJobs', {
        is: (numJobs: number) => numJobs > 0,
        then: (schema) => schema.required('Employer name is required'),
        otherwise: (schema) => schema
      })
    })
  ).default([]),
  hasSelfEmployment: yup.boolean().default(false),
  selfEmployment: yup.object({
    businessName: yup.string().when('hasSelfEmployment', {
      is: true,
      then: (schema) => schema.required('Business name is required'),
      otherwise: (schema) => schema
    }),
    businessEIN: yup.string().when('hasSelfEmployment', {
      is: true,
      then: (schema) => schema.required('Business EIN is required'),
      otherwise: (schema) => schema
    }),
    businessType: yup.string().when('hasSelfEmployment', {
      is: true,
      then: (schema) => schema.required('Business type is required'),
      otherwise: (schema) => schema
    }),
    homeOffice: yup.boolean().default(false),
    businessVehicle: yup.boolean().default(false)
  }).default({
    businessName: '',
    businessEIN: '',
    businessType: '',
    homeOffice: false,
    businessVehicle: false
  }),
  otherIncome: yup.object({
    hasInterest: yup.boolean().default(false),
    hasDividends: yup.boolean().default(false),
    hasCapitalGains: yup.boolean().default(false),
    hasRental: yup.boolean().default(false),
    hasUnemployment: yup.boolean().default(false),
    hasSocialSecurity: yup.boolean().default(false),
    hasRetirement: yup.boolean().default(false),
    hasCrypto: yup.boolean().default(false)
  }).default({
    hasInterest: false,
    hasDividends: false,
    hasCapitalGains: false,
    hasRental: false,
    hasUnemployment: false,
    hasSocialSecurity: false,
    hasRetirement: false,
    hasCrypto: false
  })
}).required() as yup.ObjectSchema<EmploymentIncomeData>;

const EmploymentIncomeStep: React.FC<FormComponentProps<EmploymentIncomeData>> = ({ data, onNext }) => {
  const { completeStep } = useWizardStore();
  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm<EmploymentIncomeData>({
    resolver: yupResolver(schema),
    defaultValues: data || {
      numJobs: 0,
      jobs: [],
      hasSelfEmployment: false,
      selfEmployment: {
        businessName: '',
        businessEIN: '',
        businessType: '',
        homeOffice: false,
        businessVehicle: false
      },
      otherIncome: {
        hasInterest: false,
        hasDividends: false,
        hasCapitalGains: false,
        hasRental: false,
        hasUnemployment: false,
        hasSocialSecurity: false,
        hasRetirement: false,
        hasCrypto: false
      }
    }
  });

  const numJobs = watch('numJobs');
  const hasSelfEmployment = watch('hasSelfEmployment');

  const onSubmit: SubmitHandler<EmploymentIncomeData> = (formData) => {
    console.log(formData);
    completeStep(4);
    onNext(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ px: 5, py: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 400, mb: 1 }}>
        Employment & Income Sources
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
        Tell us about your employment and all sources of income for the tax year.
      </Typography>

      <IncomeSource>
        <Typography variant="h6" gutterBottom>
          Employment (W-2 Forms)
        </Typography>
        <Controller
          name="numJobs"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              type="number"
              label="Number of jobs held during the tax year"
              inputProps={{ min: 0 }}
              margin="normal"
              error={!!errors.numJobs}
              helperText={errors.numJobs?.message}
            />
          )}
        />
        {Array.from({ length: numJobs }).map((_, index) => (
          <Controller
            key={index}
            name={`jobs.${index}.employerName`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={`Employer ${index + 1} Name`}
                margin="normal"
                error={!!errors.jobs?.[index]?.employerName}
                helperText={errors.jobs?.[index]?.employerName?.message}
              />
            )}
          />
        ))}
      </IncomeSource>

      <SectionDivider />

      <IncomeSource>
        <Typography variant="h6" gutterBottom>
          Self-Employment Income (1099-NEC, 1099-K)
        </Typography>
        <Controller
          name="hasSelfEmployment"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!e.target.checked) {
                      setValue('selfEmployment', {
                        businessName: '',
                        businessEIN: '',
                        businessType: '',
                        homeOffice: false,
                        businessVehicle: false
                      });
                    }
                  }}
                />
              }
              label="I have self-employment income"
            />
          )}
        />
        {hasSelfEmployment && (
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="selfEmployment.businessName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Business Name"
                      error={!!errors.selfEmployment?.businessName}
                      helperText={errors.selfEmployment?.businessName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="selfEmployment.businessEIN"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Business EIN"
                      error={!!errors.selfEmployment?.businessEIN}
                      helperText={errors.selfEmployment?.businessEIN?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="selfEmployment.businessType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Type of Business"
                      error={!!errors.selfEmployment?.businessType}
                      helperText={errors.selfEmployment?.businessType?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="selfEmployment.homeOffice"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="I use my home for business (home office deduction)"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="selfEmployment.businessVehicle"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="I use my vehicle for business purposes"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </IncomeSource>

      <SectionDivider />

      <IncomeSource>
        <Typography variant="h6" gutterBottom>
          Other Income Sources
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="otherIncome.hasInterest"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Interest Income (1099-INT)"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="otherIncome.hasDividends"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Dividend Income (1099-DIV)"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="otherIncome.hasCapitalGains"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Capital Gains/Losses (1099-B)"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="otherIncome.hasRental"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Rental Property Income"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="otherIncome.hasUnemployment"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Unemployment Benefits (1099-G)"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="otherIncome.hasSocialSecurity"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Social Security Benefits (SSA-1099)"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="otherIncome.hasRetirement"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Retirement Distributions (1099-R)"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="otherIncome.hasCrypto"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Cryptocurrency Transactions"
                />
              )}
            />
          </Grid>
        </Grid>
      </IncomeSource>
    </Box>
  );
};

export default EmploymentIncomeStep; 